import type { CoordEntry, CoordItem, LoadResult } from "./types";
import { appConfig, corsImageAssetUrl, dataAssetUrl, joinAssetUrl } from "./config";
import { fetchItemStatuses, normalizeFullCoordinateOwnershipId } from "./services/aipriDb";
import { fetchWithTimeout } from "./services/http";
import type { UserDataSource } from "./services/dataSource";

type RawItem = Omit<CoordItem, "category" | "brand">;
type RawEntry = Omit<CoordEntry, "key" | "category" | "items"> & {
  items?: RawItem[];
};
type ManifestCategory = {
  id: string;
  file: string;
  count?: number;
  thumbnailPrefix?: string;
  version?: string;
};
type DataManifest = {
  files?: string[];
  categories?: ManifestCategory[];
};

export function categoryFromUrl(url: string): string | null {
  return String(url).match(/(?:^|\/)img\/(?:thumb\/)?([^/]+)\//)?.[1] ?? null;
}

export function imageUrl(url: string): string {
  return joinAssetUrl(appConfig.imageBaseUrl, url);
}

export function originalImagePath(url: string): string {
  return url.replace(/(^|\/)img\/thumb\//, "$1img/");
}

export function originalImageUrl(url: string): string {
  return corsImageAssetUrl(originalImagePath(url));
}

function withVersion(url: string, version?: string): string {
  if (!version || /[?&]v=/.test(url)) return url;
  return `${url}${url.includes("?") ? "&" : "?"}v=${encodeURIComponent(version)}`;
}

export function normalizeEntry(raw: RawEntry, source: string, index: number, version?: string): CoordEntry {
  const rawUrl = String(raw.url ?? "");
  const urlCategory = categoryFromUrl(rawUrl);
  const category = raw.series === "primagi"
    ? String(raw.chapter ?? "").trim() || urlCategory
    : urlCategory;
  const brand = String(raw.brand ?? "");
  const name = String(raw.name ?? "");
  const url = withVersion(rawUrl, version);

  return {
    ...raw,
    key: `${source}:${index}:${url}`,
    name,
    url,
    star: String(raw.star ?? ""),
    brand,
    category,
    items: (raw.items ?? [])
      .filter((item) => item?.url && item?.term)
      .map((item) => ({
        ...item,
        name: String(item.name ?? name),
        id: String(item.id ?? ""),
        url: withVersion(String(item.url), version),
        point: String(item.point ?? ""),
        term: normalizeTerm(String(item.term)),
        category: raw.series === "primagi" ? category : (categoryFromUrl(String(item.url)) ?? category),
        brand,
      })),
  };
}

const termAliases = new Map<string, string>([
  ["アクセ", "アクセ"],
  ["アクセサリー", "アクセ"],
  ["トップス", "トップス"],
  ["上", "トップス"],
  ["ボトムス", "ボトムス"],
  ["下", "ボトムス"],
  ["スカート", "ボトムス"],
  ["パンツ", "ボトムス"],
  ["シューズ", "シューズ"],
  ["靴", "シューズ"],
  ["ワンピ", "ワンピ"],
  ["ワンピース", "ワンピ"],
]);

export function normalizeTerm(term: string): string {
  return (termAliases.get(term) ?? term) || "-";
}

async function fetchJson<T>(url: string, cache: RequestCache): Promise<T> {
  const response = await fetchWithTimeout(url, { cache });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.json() as Promise<T>;
}

export async function loadEntries(
  source: UserDataSource,
  options: { refreshStatuses?: boolean } = {},
): Promise<LoadResult> {
  let manifest: DataManifest;
  try {
    manifest = await fetchJson<DataManifest>(
      dataAssetUrl("manifest.json"),
      "no-cache"
    );
  } catch (error) {
    return {
      entries: [],
      errors: [`manifest.json: ${error instanceof Error ? error.message : String(error)}`],
    };
  }

  const files = manifest.categories?.map(({ file, version }) => ({ file, version }))
    ?? manifest.files?.map((file) => ({ file, version: undefined }))
    ?? [];
  const results = await Promise.allSettled(
    files.map(async ({ file, version }) => ({
      file,
      version,
      rows: await fetchJson<RawEntry[]>(
        withVersion(dataAssetUrl(file), version),
        "force-cache"
      ),
    }))
  );
  const entries: CoordEntry[] = [];
  const errors: string[] = [];

  for (const result of results) {
    if (result.status === "rejected") {
      errors.push(result.reason instanceof Error ? result.reason.message : String(result.reason));
      continue;
    }
    const { file, version, rows } = result.value;
    if (!Array.isArray(rows)) {
      errors.push(`${file}: 配列形式ではありません。`);
      continue;
    }
    rows.forEach((row, index) => entries.push(normalizeEntry(row, file, index, version)));
  }

  if ((source.kind === "spreadsheet" && source.sheetId) || (source.kind === "cloudflare-d1" && source.endpoint && source.token)) {
    try {
      const statuses = await fetchItemStatuses(source, {
        forceRefresh: options.refreshStatuses,
      });
      for (const entry of entries) {
        const isOaFullCoordinate = entry.category?.toLowerCase().startsWith("oa") === true
          && entry.items.some((item) => /^OAF/i.test(item.id));
        entry.items = entry.items.map((item) => {
          const status = statuses.get(item.id)
            ?? (isOaFullCoordinate ? statuses.get(normalizeFullCoordinateOwnershipId(item.id)) : undefined);
          return {
            ...item,
            owned: status?.owned ?? false,
            qr: status?.qr ?? "",
            setQR: status?.setQR ?? "",
          };
        });
      }
    } catch (error) {
      errors.push(`取得状況・QR: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  return { entries, errors };
}
